'use client'

import { type ReactNode } from 'react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Spliner from '@/lib/spline'
import Hero from '@/components/content/common/Hero'
import Container from '@/components/structure/Container'
import Authors from '@/components/content/common/Authors'
import Timestamp from '@/components/ui/Timestamp'
import { replaceTitleWordCasing } from '@/lib/formatting'
import type { Category, User } from '@/payload-types'
import type { Timestamps } from '@/types/app'

type BestHeroProps = {
  category: Category
  selected: number
  total: number
  year: number
  authors: Pick<User, 'id' | 'name' | 'image' | 'authorDescription' | 'headshot'>[]
  timestamps: Timestamps
  children?: ReactNode
}

export default function BestHero({
  category,
  selected,
  total,
  year,
  authors,
  timestamps,
  children,
}: BestHeroProps) {
  const useSingular = total === 1

  const nameLabel =
    (useSingular ? category?.nameLabelSingular : category?.nameLabelPlural) || 'Apps'

  const nameLabelLowercase = replaceTitleWordCasing(
    (useSingular
      ? category?.nameLabelSingular?.toLowerCase()
      : category?.nameLabelPlural?.toLowerCase()) || 'apps',
  )

  return (
    <Hero className="bg-gradient pt-32 md:pb-14 md:pt-36" height={{ auto: true }}>
      {({ animations }) => (
        <Container className="relative z-20">
          <Motion>
            <m.div
              style={animations}
              className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center"
            >
              <div className="h-32 overflow-hidden">
                <Spliner
                  scene="/splines/best-logo.splinecode"
                  fallback="/splines/best-logo-fallback.png"
                  size={128}
                  className="mt-3"
                  loadStrategy="after-interaction-idle"
                  showFallbackOnDesktop
                  fallbackPriority
                />
              </div>

              <h1>
                {total} Best {nameLabel} in {year}
              </h1>

              {timestamps && (
                <Timestamp
                  modified={timestamps.modified}
                  published={timestamps.published}
                  lead="Updated"
                  className="sm:hidden"
                />
              )}

              <div className="text-16 text-white/60 md:text-18">
                <p>
                  {(() => {
                    if (selected === 0) {
                      return `See how the ${total} ${nameLabelLowercase} we evaluated compare.`
                        .split('  ')
                        .join(' ')
                    }

                    if (useSingular) {
                      return `See how our top pick compares across the ${nameLabelLowercase} we evaluated.`
                        .split('  ')
                        .join(' ')
                    }

                    return `See how our top ${selected} picks compare across the ${total} ${nameLabelLowercase} we evaluated.`
                      .split('  ')
                      .join(' ')
                  })()}
                </p>
                <p>
                  Explore what each does best, where it falls short, and why it earned a spot on our{' '}
                  {year} list.
                </p>
              </div>

              {children && (
                <div className="mt-1 flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
                  {children}
                </div>
              )}

              <Authors authors={authors} theme="dark" className="mt-6" eagerImage={true} />
            </m.div>
          </Motion>
        </Container>
      )}
    </Hero>
  )
}
